import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type TopUpInput, walletApi } from '../wallet';

const keys = {
  wallet: (userId: string) => ['cafeteria', 'wallet', userId] as const,
  myWallet: ['cafeteria', 'wallet', 'me'] as const,
};

// Named "Wallet"/"TopUp" (not "Cafeteria...") since these live outside the
// category/product naming already used in use-cafeteria.ts, but still unique
// across the barrel — see that file's note about the prior name collision.
export function useWalletQuery(userId: string | undefined) {
  return useQuery({
    queryKey: keys.wallet(userId ?? ''),
    queryFn: () => walletApi.getWallet(userId as string),
    enabled: Boolean(userId),
  });
}

/** The signed-in person's own wallet — no cafeteria module needed, unlike useWalletQuery. */
export function useMyWalletQuery() {
  return useQuery({ queryKey: keys.myWallet, queryFn: () => walletApi.getMyWallet() });
}

// Takes { userId, ...input } per call (rather than binding userId at hook-creation time) so a
// single hook instance can be reused across renders; invalidates that student's wallet query.
export function useTopUpMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: TopUpInput & { userId: string }) => walletApi.topUp(userId, input),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: keys.wallet(variables.userId) });
    },
  });
}
